import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { supabase } from '../lib/supabaseClient';

function AddDogPage() {
  const [activeTab, setActiveTab] = useState('dog'); // 'dog', 'story', 'manage'
  
  // --- AUTH STATE ---
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  // --- DOG FORM STATE ---
  const [dogData, setDogData] = useState({
    name: '',
    age: '',
    size: '',
    character: '',
    status: 'In cerca di casa',
    bg: 'bg-beige',
    story: '',
    health: '',
    compatibility: '',
    is_adopted: false
  });
  const [dogImageFile, setDogImageFile] = useState(null);
  
  // --- STORY FORM STATE ---
  const [storyData, setStoryData] = useState({
    title: '',
    content: '',
    difficulties: '',
    character: '',
    progress: '',
    quote: ''
  });
  const [storyImageFile, setStoryImageFile] = useState(null);

  // --- MANAGE DOGS STATE ---
  const [dogsList, setDogsList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Fetch dogs when switching to manage tab
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (activeTab === 'manage' && session) {
      fetchDogs();
    }
  }, [activeTab, session]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setLoginError('');
    const { error } = await supabase.auth.signInWithPassword({
      email: loginEmail,
      password: loginPassword,
    });
    if (error) setLoginError(error.message);
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const fetchDogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('dogs').select('*').order('id', { ascending: false });
      if (error) throw error;
      setDogsList(data || []);
    } catch (err) {
      console.error('Error fetching dogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdoptedStatus = async (dog) => {
    try {
      const newStatus = !dog.is_adopted;
      const { error } = await supabase.from('dogs').update({ is_adopted: newStatus }).eq('id', dog.id);
      if (error) throw error;
      
      // Update local state
      setDogsList(dogsList.map(d => d.id === dog.id ? { ...d, is_adopted: newStatus } : d));
    } catch (err) {
      console.error('Error updating status:', err);
      alert("Errore durante l'aggiornamento. Ricordati di eseguire il comando SQL per la colonna is_adopted.");
    }
  };

  // --- HANDLERS ---
  const handleDogChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setDogData({ ...dogData, [e.target.name]: value });
  };
  const handleDogImageChange = (e) => { if (e.target.files && e.target.files[0]) setDogImageFile(e.target.files[0]); };

  const handleStoryChange = (e) => setStoryData({ ...storyData, [e.target.name]: e.target.value });
  const handleStoryImageChange = (e) => { if (e.target.files && e.target.files[0]) setStoryImageFile(e.target.files[0]); };

  const handleDogSubmit = async (e) => {
    e.preventDefault();
    if (!dogImageFile) return setMessage("Errore: Devi selezionare un'immagine per il cane.");
    
    setLoading(true); setMessage('');
    try {
      const fileExt = dogImageFile.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `dogs/${fileName}`;

      const { error: uploadError } = await supabase.storage.from('dogs').upload(filePath, dogImageFile);
      if (uploadError) throw new Error(`STORAGE_ERROR: ${uploadError.message}`);

      const { data: publicUrlData } = supabase.storage.from('dogs').getPublicUrl(filePath);
      
      const { error: insertError } = await supabase.from('dogs').insert([{ ...dogData, image: publicUrlData.publicUrl }]);
      if (insertError) throw new Error(`DB_ERROR: ${insertError.message}`);

      setMessage('Cane inserito con successo! Immagine caricata.');
      setDogData({ name: '', age: '', size: '', character: '', status: 'In cerca di casa', bg: 'bg-beige', story: '', health: '', compatibility: '', is_adopted: false });
      setDogImageFile(null);
      document.getElementById('dog-image-upload').value = '';
    } catch (err) {
      console.error('Error:', err);
      setMessage(`Errore: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStorySubmit = async (e) => {
    e.preventDefault();
    if (!storyImageFile) return setMessage("Errore: Devi selezionare un'immagine per la storia.");

    setLoading(true); setMessage('');
    try {
      const fileExt = storyImageFile.name.split('.').pop();
      const fileName = `story_${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `dogs/${fileName}`; // using same bucket

      const { error: uploadError } = await supabase.storage.from('dogs').upload(filePath, storyImageFile);
      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage.from('dogs').getPublicUrl(filePath);
      
      const { error: insertError } = await supabase.from('success_stories').insert([{ ...storyData, image: publicUrlData.publicUrl }]);
      if (insertError) throw insertError;

      setMessage('Storia inserita con successo! Immagine caricata.');
      setStoryData({ title: '', content: '', difficulties: '', character: '', progress: '', quote: '' });
      setStoryImageFile(null);
      document.getElementById('story-image-upload').value = '';
    } catch (err) {
      console.error('Error:', err);
      setMessage(`Errore durante l'inserimento della storia: ${err.message || 'Errore sconosciuto. Controlla la console per i dettagli.'}`);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return <div style={{ padding: '100px', textAlign: 'center' }}>Controllo credenziali in corso...</div>;

  if (!session) {
    return (
      <>
        <Navbar />
        <div className="container" style={{ padding: '120px 24px', maxWidth: '500px' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '24px' }}>Accesso Area Riservata</h2>
          {loginError && (
             <div style={{ padding: '16px', marginBottom: '24px', borderRadius: '8px', backgroundColor: '#ffebee', color: '#c62828' }}>
               Errore: {loginError === 'Invalid login credentials' ? 'Credenziali non valide.' : loginError}
             </div>
          )}
          <form onSubmit={handleLogin} style={{ background: 'var(--color-bg-primary)', padding: '32px', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Email</label>
              <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required style={{ width: '100%', padding: '16px', borderRadius: '12px', border: '1px solid #ddd' }} />
            </div>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Password</label>
              <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required style={{ width: '100%', padding: '16px', borderRadius: '12px', border: '1px solid #ddd' }} />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '16px', marginTop: '16px' }}>Accedi</button>
          </form>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container" style={{ padding: '80px 24px', maxWidth: '800px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 className="section-title" style={{ textAlign: 'left', margin: 0 }}>
            Area di Amministrazione
          </h1>
          <button onClick={handleLogout} className="btn btn-outline" style={{ padding: '8px 16px', fontSize: '14px', borderColor: '#c62828', color: '#c62828' }}>
            Esci (Logout)
          </button>
        </div>
        
        {message && (
          <div style={{ padding: '16px', marginBottom: '24px', borderRadius: '8px', backgroundColor: message.includes('Errore') ? '#ffebee' : '#e8f5e9', color: message.includes('Errore') ? '#c62828' : '#2e7d32' }}>
            {message}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', flexWrap: 'wrap' }}>
          <button 
            className={activeTab === 'dog' ? 'btn btn-primary' : 'btn btn-outline'}
            onClick={() => { setActiveTab('dog'); setMessage(''); }}
            style={{ flex: 1, minWidth: '200px' }}
          >
            Aggiungi Cane
          </button>
          <button 
            className={activeTab === 'story' ? 'btn btn-primary' : 'btn btn-outline'}
            onClick={() => { setActiveTab('story'); setMessage(''); }}
            style={{ flex: 1, minWidth: '200px' }}
          >
            Aggiungi Storia
          </button>
          <button 
            className={activeTab === 'manage' ? 'btn btn-primary' : 'btn btn-outline'}
            onClick={() => { setActiveTab('manage'); setMessage(''); }}
            style={{ flex: 1, minWidth: '200px' }}
          >
            Gestisci Cani
          </button>
        </div>

        <div style={{ background: 'var(--color-bg-primary)', padding: '48px', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}>
          {activeTab === 'dog' && (
            <form onSubmit={handleDogSubmit}>
              <h2 style={{ marginBottom: '24px' }}>Inserisci un nuovo cane</h2>
              <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nome *</label>
                  <input type="text" name="name" value={dogData.name} onChange={handleDogChange} required placeholder="Es. Fido" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Età *</label>
                  <input type="text" name="age" value={dogData.age} onChange={handleDogChange} required placeholder="Es. 2 anni" />
                </div>
              </div>

              <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Taglia *</label>
                  <input type="text" name="size" value={dogData.size} onChange={handleDogChange} required placeholder="Es. Media" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Carattere *</label>
                  <input type="text" name="character" value={dogData.character} onChange={handleDogChange} required placeholder="Es. Vivace e dolce" />
                </div>
              </div>

              <div className="form-group" style={{ display: 'flex', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Stato *</label>
                  <input type="text" name="status" value={dogData.status} onChange={handleDogChange} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Colore Sfondo Card</label>
                  <input type="text" name="bg" value={dogData.bg} onChange={handleDogChange} list="bg-options" style={{ width: '100%', padding: '16px', border: '1px solid #ddd', borderRadius: '12px', fontFamily: 'var(--font-secondary)' }} placeholder="Es. bg-beige oppure #FF5733" />
                  <datalist id="bg-options">
                    <option value="bg-beige">Beige predefinito</option>
                    <option value="bg-green">Verde</option>
                    <option value="bg-peach">Pesca</option>
                    <option value="#FFDAB9">PeachPuff (Es. Hex)</option>
                  </datalist>
                </div>
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Foto del Cane *</label>
                <input id="dog-image-upload" type="file" accept="image/*" onChange={handleDogImageChange} required style={{ width: '100%', padding: '16px', border: '1px solid #ddd', borderRadius: '12px', background: '#fff' }} />
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Storia (Passato) *</label>
                <textarea name="story" value={dogData.story} onChange={handleDogChange} required placeholder="Descrivi il suo passato..." style={{ minHeight: '120px' }}></textarea>
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Condizioni di Salute *</label>
                <textarea name="health" value={dogData.health} onChange={handleDogChange} required placeholder="Es. Vaccinato, microchippato..." style={{ minHeight: '80px' }}></textarea>
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Compatibilità *</label>
                <textarea name="compatibility" value={dogData.compatibility} onChange={handleDogChange} required placeholder="Es. Perfetto con i bambini..." style={{ minHeight: '80px' }}></textarea>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '16px', backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: '12px' }}>
                  <input type="checkbox" name="is_adopted" checked={dogData.is_adopted} onChange={handleDogChange} style={{ transform: 'scale(1.5)' }} />
                  <span style={{ fontWeight: '500' }}>Segna come "Già Adottato"</span>
                </label>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', padding: '16px', fontSize: '18px', marginTop: '24px' }}>
                {loading ? 'Inserimento in corso...' : 'Aggiungi Cane'}
              </button>
            </form>
          )}

          {activeTab === 'story' && (
            <form onSubmit={handleStorySubmit}>
              <h2 style={{ marginBottom: '24px' }}>Inserisci una Storia a Lieto Fine</h2>
              
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Titolo *</label>
                <input type="text" name="title" value={storyData.title} onChange={handleStoryChange} required placeholder="Es. La rinascita di Baloo" />
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Testo della storia *</label>
                <textarea name="content" value={storyData.content} onChange={handleStoryChange} required placeholder="Racconta la storia dall'arrivo al canile fino all'adozione..." style={{ minHeight: '120px' }}></textarea>
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Punto elenco 1: Difficoltà superate *</label>
                <input type="text" name="difficulties" value={storyData.difficulties} onChange={handleStoryChange} required placeholder="Es. Paura degli estranei e malnutrizione." />
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Punto elenco 2: Carattere *</label>
                <input type="text" name="character" value={storyData.character} onChange={handleStoryChange} required placeholder="Es. Adesso è il re delle coccole da divano." />
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Punto elenco 3: Progressi *</label>
                <input type="text" name="progress" value={storyData.progress} onChange={handleStoryChange} required placeholder="Es. Ha imparato ad andare al guinzaglio." />
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Citazione finale *</label>
                <textarea name="quote" value={storyData.quote} onChange={handleStoryChange} required placeholder="Inserisci una frase a effetto (non serve mettere le virgolette, verranno aggiunte in automatico)..." style={{ minHeight: '80px' }}></textarea>
              </div>

              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Foto prima/dopo o felice *</label>
                <input id="story-image-upload" type="file" accept="image/*" onChange={handleStoryImageChange} required style={{ width: '100%', padding: '16px', border: '1px solid #ddd', borderRadius: '12px', background: '#fff' }} />
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', padding: '16px', fontSize: '18px', marginTop: '24px', backgroundColor: 'var(--color-primary-dark)' }}>
                {loading ? 'Inserimento in corso...' : 'Aggiungi Storia'}
              </button>
            </form>
          )}

          {activeTab === 'manage' && (
            <div>
              <h2 style={{ marginBottom: '24px' }}>Gestione Cani</h2>
              <p style={{ marginBottom: '24px', color: 'var(--color-text-light)' }}>
                Qui puoi segnare i cani come "Adottati". Questo aggiungerà un banner sulle loro foto e rimuoverà il pulsante di adozione.
              </p>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Caricamento cani...</div>
              ) : dogsList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Nessun cane trovato.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {dogsList.map(dog => (
                    <div key={dog.id} style={{ display: 'flex', alignItems: 'center', padding: '16px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #eee' }}>
                      <img src={dog.image} alt={dog.name} style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover', marginRight: '16px' }} />
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>{dog.name}</h4>
                        <span style={{ fontSize: '14px', color: '#666' }}>{dog.status}</span>
                      </div>
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 16px', backgroundColor: dog.is_adopted ? '#e8f5e9' : '#f5f5f5', borderRadius: '24px', transition: 'all 0.2s' }}>
                          <input 
                            type="checkbox" 
                            checked={dog.is_adopted || false} 
                            onChange={() => toggleAdoptedStatus(dog)} 
                            style={{ transform: 'scale(1.2)' }} 
                          />
                          <span style={{ fontWeight: '500', color: dog.is_adopted ? '#2e7d32' : '#333' }}>
                            {dog.is_adopted ? 'Adottato 🎉' : 'In cerca di casa'}
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AddDogPage;
